
import { useState, useEffect, useMemo } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import CampusHeader from '../../components/feature/CampusHeader';
import Footer from '../../components/feature/Footer';
import { signUpWithEmail, signInWithEmail, signOutUser, subscribeAuth, getUserRole } from '../../services/auth';
import { notifyAdmins } from '../../services/email';
import type { UserRole } from '../../services/auth';
import { getAllListings, createListing } from '../../services/listings'
import type { Listing } from '../../services/listings'
import { auth, db } from '../../lib/firebase'
import { uploadListingImage, uploadStudentIdImage } from '../../services/storage'
import { useNavigate, useLocation } from 'react-router-dom';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import ListingSuccessModal from '../../components/modals/ListingSuccessModal';
import SellerOnboardingSuccessModal from '../../components/modals/SellerOnboardingSuccessModal';
import { NIGERIAN_UNIVERSITIES } from '../../data/nigerianUniversities';
import { sendWelcomeEmail } from '../../services/email';
import { sellerProductAddedEmail } from '../../services/email';
import { toUserMessage } from '../../utils/errors';

const SellPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const [showSignUp, setShowSignUp] = useState(false);
  const [showLogin, setShowLogin] = useState(false);
  const [showVerification, setShowVerification] = useState(false);
  const [currentStep, setCurrentStep] = useState('welcome'); // welcome, signup, login, verification, dashboard
  const [popularCategories, setPopularCategories] = useState<string[]>([])
  const [activeListingsCount, setActiveListingsCount] = useState(0)
  const [totalEarnings, setTotalEarnings] = useState(0)
  const [profileViews, setProfileViews] = useState(0)
  const [showPhotoTipsModal, setShowPhotoTipsModal] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);

  // Listing creation modal state
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [newListing, setNewListing] = useState({
    title: '',
    price: '',
    category: '',
    condition: '',
    location: '',
    description: ''
  })
  const [listingImages, setListingImages] = useState<File[]>([])
  const [uploading, setUploading] = useState(false)
  const [uploadError, setUploadError] = useState<string | null>(null)
  const [showSuccessModal, setShowSuccessModal] = useState(false)
  const [createdListingId, setCreatedListingId] = useState<string | null>(null)
  const [showSellerSuccessModal, setShowSellerSuccessModal] = useState(false)

  // Combined single auth subscription hook (fixed duplicate)
  useEffect(() => {
    const unsub = subscribeAuth(async (user) => {
      const params = new URLSearchParams(location.search);
      const stepParam = params.get('step');
      const resumeDocs = params.get('resume');

      // If explicitly resuming docs or hitting verification step, honor it
      if (stepParam === 'verification' || resumeDocs === 'docs') {
        setCurrentStep('verification');
        return;
      }

      if (user) {
        setIsLoggedIn(true);
        const { role, status } = await getUserRole(user.uid);

        // Allow sellers without submitted ID to open verification here
        if (role === 'seller') {
          try {
            const snap = await getDoc(doc(db, 'users', user.uid));
            const data = snap.data();
            const hasId = Boolean(data?.studentIdUrl);
            if (!hasId) {
              setCurrentStep('verification');
              return;
            }
          } catch {}

          // Otherwise route by status
          if (status === 'pending') {
            navigate('/pending-verification');
          } else {
            navigate('/seller-dashboard');
          }
          return;
        }

        // If buyer and they landed here, default to welcome unless query forced verification above
        if (role === 'buyer') {
          setCurrentStep('welcome');
        }
      } else {
        setIsLoggedIn(false);
        setCurrentStep('welcome');
      }
    });
    return () => unsub();
  }, [navigate, location.search]);

  // Moved listing logic to useEffect (fixed IIFE re-render issue)
  useEffect(() => {
    const fetchListingData = async () => {
      const listings = await getAllListings();
      const catCounts = listings.reduce((acc, l) => {
        const cat = l.category?.trim();
        if (!cat) return acc;
        acc[cat] = (acc[cat] || 0) + 1;
        return acc;
      }, {} as Record<string, number>);
      const sorted = Object.entries(catCounts).sort((a,b)=>b[1]-a[1]).map(([name])=>name);
      setPopularCategories(sorted.slice(0,4));

      const current = auth.currentUser;
      const name = current?.displayName?.trim();
      const email = current?.email?.trim();
      const owned = listings.filter(l => {
        const seller = l.seller?.trim();
        if (!seller) return false;
        return (name && seller === name) || (email && seller === email);
      });
      setActiveListingsCount(owned.length);

      setTotalEarnings(0);
      setProfileViews(0);
    };

    if (isLoggedIn) fetchListingData();
  }, [isLoggedIn]);

  const [loading, setLoading] = useState(false);
  const [signUpData, setSignUpData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    fullName: '',
    username: '',
    phone: '',
    referralCode: ''
  });
  const [signupStep, setSignupStep] = useState(0);
  const [slideDir, setSlideDir] = useState(1);
  const [stepError, setStepError] = useState<string | null>(null);

  const SIGNUP_TOTAL_STEPS = 6;

  const validateSignupStep = (step: number): string | null => {
    if (step === 0) {
      if (!signUpData.fullName.trim()) return 'Please enter your full name (used for verification).';
      if (signUpData.fullName.trim().length < 2) return 'That name seems a little short — try your full name.';
      if (!signUpData.username.trim()) return 'Please choose a store/vendor name — this is what buyers will see.';
      if (signUpData.username.trim().length < 2) return 'Your store name is too short.';
    }
    if (step === 1) {
      if (!signUpData.email.trim()) return 'We need an email to reach you.';
      if (!/^\S+@\S+\.\S+$/.test(signUpData.email.trim())) return 'That email doesn’t look quite right.';
      const phoneErr = validatePhone(signUpData.phone);
      if (phoneErr) return phoneErr;
    }
    if (step === 2) {
      const err = validatePassword(signUpData.password);
      if (err) return err;
      if (signUpData.password !== signUpData.confirmPassword) return 'Passwords don’t match — try again.';
    }
    if (step === 3) {
      if (!verificationData.university.trim()) return 'Please select your school.';
      if (!verificationData.studentNumber.trim()) return 'Please enter your student ID number.';
    }
    if (step === 4) {
      if (!verificationData.studentId) return 'Please upload a clear photo of your student ID — it’s required to sell.';
    }
    return null;
  };

  const goNextSignup = () => {
    const err = validateSignupStep(signupStep);
    if (err) { setStepError(err); return; }
    setStepError(null);
    setSlideDir(1);
    setSignupStep(s => Math.min(s + 1, SIGNUP_TOTAL_STEPS - 1));
  };

  const goPrevSignup = () => {
    setStepError(null);
    setSlideDir(-1);
    setSignupStep(s => Math.max(s - 1, 0));
  };

  const passwordStrength = (() => {
    const p = signUpData.password;
    let score = 0;
    if (p.length >= 8) score++;
    if (/\d/.test(p)) score++;
    if (/[^A-Za-z0-9]/.test(p)) score++;
    if (p.length >= 12) score++;
    return score; // 0..4
  })();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const ref = params.get('ref');
    if (ref) {
      setSignUpData(prev => ({ ...prev, referralCode: ref }));
    }
  }, [location.search]);

  const [loginData, setLoginData] = useState({
    email: '',
    password: ''
  });
  const [loginError, setLoginError] = useState<string | null>(null);
  const [verifyError, setVerifyError] = useState<string | null>(null);
  const [showSignupPwd, setShowSignupPwd] = useState(false);
  const [showSignupConfirmPwd, setShowSignupConfirmPwd] = useState(false);
  const [showLoginPwd, setShowLoginPwd] = useState(false);

  const [verificationData, setVerificationData] = useState({
    studentId: null as File | null,
    university: '',
    studentNumber: ''
  });
  const [showUniSuggestions, setShowUniSuggestions] = useState(false);

  const uniSuggestions = useMemo(() => {
    const q = verificationData.university.trim().toLowerCase();
    if (!q) return [] as string[];
    return NIGERIAN_UNIVERSITIES
      .filter(u => u.toLowerCase().includes(q))
      .slice(0, 8);
  }, [verificationData.university]);

  const validatePassword = (password: string): string | null => {
    if (password.length < 8) return 'Password must be at least 8 characters.';
    if (!/\d/.test(password)) return 'Password must contain at least 1 number.';
    if (!/[^A-Za-z0-9]/.test(password)) return 'Password must contain at least 1 symbol.';
    return null;
  };

  const validatePhone = (raw: string): string | null => {
    if (!raw.trim()) return 'A phone or WhatsApp number helps buyers reach you fast.';
    const digits = raw.replace(/\D/g, '');
    if (digits.length < 10 || digits.length > 15) return 'That number doesn’t look right — enter your full phone/WhatsApp number.';
    return null;
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setStepError(null);
    const email = signUpData.email.trim();
    const password = signUpData.password;
    const fullName = signUpData.fullName.trim();
    const university = verificationData.university.trim();
    const studentNumber = verificationData.studentNumber.trim();
    const idFile = verificationData.studentId;
    if (!email || !password || !fullName) {
      setStepError('Please fill all required fields.');
      return;
    }
    if (password !== signUpData.confirmPassword) {
      setStepError('Passwords don’t match — try again.');
      return;
    }
    const passwordError = validatePassword(password);
    if (passwordError) {
      setStepError(passwordError);
      return;
    }
    // ID + school are collected in the wizard, so the seller account is created WITH an
    // ID — a seller can never exist without one. Guard here as a backstop.
    if (!university) { setStepError('Please select your school.'); setSignupStep(3); return; }
    if (!idFile) { setStepError('Please upload your student ID — it’s required to sell.'); setSignupStep(4); return; }

    setLoading(true);

    // 1) Create the seller account. If this fails, stop and show why.
    try {
      await signUpWithEmail(
        email,
        password,
        'seller',
        { fullName, username: signUpData.username.trim(), phone: signUpData.phone.trim(), university, referredByCode: signUpData.referralCode || undefined }
      );
    } catch (err: any) {
      console.error('Sign up failed:', err);
      setStepError(toUserMessage(err));
      setLoading(false);
      return;
    }

    // 2) Attach the uploaded ID and mark pending review. If the upload fails, DON'T
    //    dead-end: the account already exists and the seller dashboard will prompt
    //    them to re-upload, so we still move them forward.
    let idAttached = false;
    try {
      const user = auth.currentUser;
      if (user) {
        const studentIdUrl = await uploadStudentIdImage(idFile, user.uid);
        await setDoc(doc(db, 'users', user.uid), {
          university,
          studentNumber,
          studentIdUrl,
          status: 'pending',
          role: 'seller',
          verificationSubmittedAt: new Date().toISOString(),
        }, { merge: true });
        idAttached = true;

        notifyAdmins({
          event: 'new_seller_verification',
          subject: `New seller awaiting approval: ${fullName || email}`,
          text: 'A new seller signed up and submitted verification documents and is pending admin review.',
          meta: { uid: user.uid, email, name: fullName, university, studentNumber, studentIdUrl },
        });
      }
    } catch (e) {
      console.warn('ID upload failed at signup; seller can re-upload from the dashboard', e);
    }

    setLoading(false);
    // If the ID didn't attach, send them to re-upload; otherwise straight to verify email.
    const redirect = idAttached ? '/seller-dashboard' : '/sell?step=verification&resume=docs';
    navigate(`/auth/email-verification?redirect=${encodeURIComponent(redirect)}`, { replace: true });
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoginError(null);
    try {
      const user = await signInWithEmail(loginData.email, loginData.password);
      setIsLoggedIn(true);
      const { role, status } = await getUserRole(user.uid);
      try { await sendWelcomeEmail(user.uid, user.email || loginData.email, user.displayName || undefined, role === 'seller' ? 'seller' : 'buyer'); } catch {}
      if (role === 'seller') {
        if (status === 'pending') {
          navigate('/pending-verification');
        } else {
          navigate('/seller-dashboard');
        }
      } else {
        navigate('/buyer-dashboard');
      }
    } catch (err: any) {
      console.error('Login failed:', err);
      setLoginError(toUserMessage(err));
    }
  };

  useEffect(() => {
    const checkExistingVerification = async () => {
      const u = auth.currentUser;
      if (!u) return;
      try {
        const snap = await getDoc(doc(db, 'users', u.uid));
        const data = snap.data();
        if (data?.studentIdUrl && data?.status !== 'rejected') {
          setShowVerification(false);
        }
      } catch {}
    };
    checkExistingVerification();
  }, [isLoggedIn]);
  const handleVerification = async (e: React.FormEvent) => {
    e.preventDefault();
    setVerifyError(null);
    setLoading(true);

    try {
      const user = auth.currentUser;
      if (!user) {
        throw new Error("User not logged in");
      }
    
    // Prevent resubmission if student ID already exists, unless rejected
    const existingSnap = await getDoc(doc(db, 'users', user.uid));
    const existing = existingSnap.data();
    if (existing?.studentIdUrl && existing?.status !== 'rejected') {
      alert('Your student ID is already submitted and pending or approved. No need to re-upload.');
      if (existing?.status === 'approved') {
        navigate('/seller-dashboard');
      } else {
        navigate('/pending-verification');
      }
      return;
    }

    // ID upload is COMPULSORY — a seller cannot be enabled without a student ID.
    // Without this guard, submitting with no file wrote studentIdUrl:'' but still set
    // role:'seller'/status:'pending', enabling an unverified seller.
    if (!verificationData.studentId && !existing?.studentIdUrl) {
      setVerifyError('Please upload a clear photo of your student ID — it is required to become a seller.');
      return; // finally{} resets loading
    }

    // Upload student ID image. Preserve an existing ID if they didn't pick a new file.
    let studentIdUrl = existing?.studentIdUrl || '';
    if (verificationData.studentId) {
      studentIdUrl = await uploadStudentIdImage(verificationData.studentId, user.uid);
    }
    if (!studentIdUrl) {
      setVerifyError('Please upload a clear photo of your student ID — it is required to become a seller.');
      return;
    }

    // Backfill identity fields if the existing user doc is missing them.
    // Without these, admins see "Unknown User" / empty email and approval emails
    // silently drop because there's no recipient.
    const backfill: Record<string, any> = {};
    if (!existing?.email && user.email) backfill.email = user.email;
    if (!existing?.fullName && user.displayName) backfill.fullName = user.displayName;
    if (!existing?.createdAt) backfill.createdAt = Date.now();

    // Update user document with university and verification data
    await setDoc(doc(db, "users", user.uid), {
      ...backfill,
      university: verificationData.university,
      studentNumber: verificationData.studentNumber,
      studentIdUrl: studentIdUrl,
      status: 'pending', // Set status to pending for admin review
      role: 'seller', // Set role to seller
      verificationSubmittedAt: new Date().toISOString()
    }, { merge: true });

    // Notify the two admin emails about the new seller awaiting review.
    notifyAdmins({
      event: 'new_seller_verification',
      subject: `New seller awaiting approval: ${existing?.fullName || user.displayName || user.email || user.uid}`,
      text: 'A buyer just submitted seller verification documents and is pending admin review.',
      meta: {
        uid: user.uid,
        email: existing?.email || user.email || '',
        name: existing?.fullName || user.displayName || '',
        university: verificationData.university || '',
        studentNumber: verificationData.studentNumber || '',
        studentIdUrl,
      },
    });

    setIsVerified(true);
    setShowSellerSuccessModal(true);
    // Navigate to seller dashboard (shows pending banner until approved)
    // navigate('/seller-dashboard');
    } catch (error: any) {
      console.error('Error during verification:', error);
      setVerifyError(toUserMessage(error) || 'Failed to submit verification. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleStudentIdUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setVerificationData(prev => ({
        ...prev,
        studentId: e.target.files![0]
      }));
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    const v = name === 'condition' ? value.toLowerCase() : value;
    setNewListing(prev => ({ ...prev, [name]: v }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) return;
    // Capture synchronously (resetting value below clears the FileList), append so
    // multiple picks accumulate, and reset so the same file can be re-selected.
    const picked = Array.from(e.target.files);
    setSelectedFiles(prev => [...prev, ...picked]);
    e.target.value = '';
  };

  const handleRemoveFile = (index: number) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = auth.currentUser;
      if (!user) {
        throw new Error("User not logged in");
      }
      const { role, status } = await getUserRole(user.uid);
      if (role !== 'seller') {
        alert('Only verified sellers can post listings. Please complete verification.');
        navigate('/sell?step=verification');
        setLoading(false);
        return;
      }
      // Gate: a seller must be APPROVED before they can list anything.
      if (status === 'pending' || status === 'rejected' || !status) {
        alert(status === 'rejected'
          ? 'Your seller application was rejected, so you cannot list items. Please contact support.'
          : 'Your seller profile is still pending admin approval. You can list items once you are approved.');
        navigate('/pending-verification');
        setLoading(false);
        return;
      }
      const listingData = {
        title: newListing.title.trim(),
        price: Number(newListing.price),
        category: newListing.category || undefined,
        condition: newListing.condition || undefined,
        location: newListing.location || undefined,
        description: newListing.description || undefined,
      };
      const id = await createListing(listingData, selectedFiles, user);
      try {
        if (user?.email) {
          await sellerProductAddedEmail(user.email, listingData.title);
        }
      } catch (e) {
        console.warn('Failed to send seller product-added email', e);
      }
      setNewListing({ title: '', price: '', category: '', condition: '', location: '', description: '' });
      setSelectedFiles([]);
      setCreatedListingId(id);
      setShowSuccessModal(true);
    } catch (error) {
      console.error('Error creating listing:', error);
      alert('Failed to create listing.');
    } finally {
      setLoading(false);
    }
  };

  // Listing image selection
  const handleListingImagesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) return;
    // Capture synchronously, append across picks, reset so re-selecting works.
    const picked = Array.from(e.target.files);
    setListingImages(prev => [...prev, ...picked]);
    e.target.value = '';
  };

  // Create listing handler
  const handleCreateListing = async (e: React.FormEvent) => {
    e.preventDefault();
    setUploadError(null);
    const user = auth.currentUser;
    if (!user) {
      setUploadError('Please login to post a listing.');
      return;
    }
    try {
      setUploading(true);
      const { role, status } = await getUserRole(user.uid);
      if (role !== 'seller') {
        setUploadError('Only verified sellers can post listings. Please complete verification.');
        navigate('/sell?step=verification');
        setUploading(false);
        return;
      }
      // Gate: a seller must be APPROVED before they can list anything.
      if (status === 'pending' || status === 'rejected' || !status) {
        setUploadError(status === 'rejected'
          ? 'Your seller application was rejected, so you cannot list items.'
          : 'Your seller profile is still pending admin approval. You can list items once approved.');
        navigate('/pending-verification');
        setUploading(false);
        return;
      }
      const listingData = {
        title: newListing.title.trim(),
        price: Number(newListing.price),
        category: newListing.category || undefined,
        condition: newListing.condition || undefined,
        location: newListing.location || undefined,
        description: newListing.description || undefined
      };
      const id = await createListing(listingData, listingImages, user);
      try {
        if (user?.email) {
          await sellerProductAddedEmail(user.email, listingData.title);
        }
      } catch (e) {
        console.warn('Failed to send seller product-added email', e);
      }
      setShowCreateModal(false);
      setListingImages([]);
      setNewListing({ title: '', price: '', category: '', condition: '', location: '', description: '' });
      setCreatedListingId(id);
      setShowSuccessModal(true);
    } catch (err: any) {
      setUploadError(err?.message || 'Failed to create listing.');
    } finally {
      setUploading(false);
    }
  };

  // Welcome Screen
  if (currentStep === 'welcome') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-50 to-blue-50">
        <CampusHeader />
        
        <main className="max-w-4xl mx-auto px-4 py-8 md:py-12">
          <div className="text-center mb-8 md:mb-12">
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Start Selling on Campus Market</h1>
            <p className="text-lg md:text-xl text-gray-600 mb-8">Turn your unused items into cash. Join thousands of students already selling!</p>
            
            <div className="bg-white rounded-2xl shadow-xl p-6 md:p-8 mb-8">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 mb-8">
                <div className="text-center">
                  <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="ri-user-add-line text-2xl text-teal-600"></i>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">1. Create Account</h3>
                  <p className="text-gray-600">Quick sign up with email or Google</p>
                </div>
                
                <div className="text-center">
                  <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="ri-shield-check-line text-2xl text-blue-600"></i>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">2. Verify Student Status</h3>
                  <p className="text-gray-600">Upload student ID for verification</p>
                </div>
                
                <div className="text-center">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="ri-store-line text-2xl text-green-600"></i>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">3. Start Selling</h3>
                  <p className="text-gray-600">Post your first item for free!</p>
                </div>
              </div>

              <div className="bg-teal-50 rounded-lg p-6 mb-8">
                <h4 className="text-lg font-semibold text-teal-900 mb-2">🎉 Free Tier Benefits</h4>
                <ul className="text-teal-800 space-y-1">
                  <li>• 1 active listing</li>
                  <li>• 5 days duration</li>
                  <li>• Direct buyer contact</li>
                  <li>• Safe meeting spots</li>
                </ul>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button
                  onClick={() => setCurrentStep('signup')}
                  className="px-8 py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors font-medium whitespace-nowrap cursor-pointer"
                >
                  Create Account
                </button>
                <span className="hidden" aria-hidden="true"></span>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-white rounded-lg p-6 shadow-md">
                <h4 className="text-lg font-semibold text-gray-900 mb-3">Why Students Love Us</h4>
                <ul className="text-gray-600 space-y-2 text-left">
                  <li className="flex items-center"><i className="ri-check-line text-green-500 mr-2"></i>100% Free to start</li>
                  <li className="flex items-center"><i className="ri-check-line text-green-500 mr-2"></i>Campus-only verified buyers</li>
                  <li className="flex items-center"><i className="ri-check-line text-green-500 mr-2"></i>Safe meeting locations</li>
                  <li className="flex items-center"><i className="ri-check-line text-green-500 mr-2"></i>AI-powered price suggestions</li>
                </ul>
              </div>

              <div className="bg-white rounded-lg p-6 shadow-md">
                <h4 className="text-lg font-semibold text-gray-900 mb-3">Popular Categories</h4>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  {popularCategories.map(cat => (
                    <span key={cat} className="bg-gray-100 px-3 py-1 rounded-full text-gray-700">{cat}</span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </main>

        <Footer />
      </div>
    );
  }

  // Sign Up Screen
  if (currentStep === 'signup') {
    return (
      <div className="min-h-screen bg-white">
        <CampusHeader />
        
        <main className="max-w-md mx-auto px-4 py-8 md:py-12">
          <div className="bg-white rounded-2xl shadow-xl p-6 md:p-8 overflow-hidden">
            <div className="mb-6">
              <div className="flex items-center justify-between text-xs font-medium text-gray-500 mb-2">
                <span>Step {signupStep + 1} of {SIGNUP_TOTAL_STEPS}</span>
                <span>{Math.round(((signupStep + 1) / SIGNUP_TOTAL_STEPS) * 100)}%</span>
              </div>
              <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                <motion.div
                  initial={false}
                  animate={{ width: `${((signupStep + 1) / SIGNUP_TOTAL_STEPS) * 100}%` }}
                  transition={{ type: 'spring', stiffness: 120, damping: 20 }}
                  className="h-full bg-gradient-to-r from-teal-400 to-emerald-500 rounded-full"
                />
              </div>
            </div>

            <div className="relative min-h-[320px]">
              <AnimatePresence mode="wait" custom={slideDir}>
                <motion.div
                  key={signupStep}
                  custom={slideDir}
                  initial={{ x: slideDir > 0 ? 60 : -60, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  exit={{ x: slideDir > 0 ? -60 : 60, opacity: 0 }}
                  transition={{ type: 'spring', stiffness: 260, damping: 26 }}
                  className="space-y-5"
                >
                  {signupStep === 0 && (
                    <div className="space-y-5">
                      <div className="text-center">
                        <div className="text-4xl mb-2">👋</div>
                        <h1 className="text-2xl font-bold text-gray-900">What should we call you?</h1>
                        <p className="text-gray-500 text-sm mt-1">First, let’s get the basics.</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Full name</label>
                        <input
                          type="text"
                          autoFocus
                          value={signUpData.fullName}
                          onChange={(e) => setSignUpData(prev => ({ ...prev, fullName: e.target.value }))}
                          className="w-full px-4 py-3 text-lg border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                          placeholder="Your real full name"
                        />
                        <p className="text-xs text-gray-400 mt-1">Private — used for verification only.</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Store / vendor name</label>
                        <input
                          type="text"
                          value={signUpData.username}
                          onChange={(e) => setSignUpData(prev => ({ ...prev, username: e.target.value }))}
                          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); goNextSignup(); } }}
                          className="w-full px-4 py-3 text-lg border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                          placeholder="e.g. Dave's Gadgets"
                        />
                        <p className="text-xs text-teal-600 mt-1">This is the name buyers see on the marketplace.</p>
                      </div>
                    </div>
                  )}

                  {signupStep === 1 && (
                    <div className="space-y-5">
                      <div className="text-center">
                        <div className="text-4xl mb-2">📬</div>
                        <h1 className="text-2xl font-bold text-gray-900">How can buyers reach you?</h1>
                        <p className="text-gray-500 text-sm mt-1">Nice to meet you, {signUpData.fullName.split(' ')[0] || 'friend'}!</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                        <input
                          type="email"
                          autoFocus
                          value={signUpData.email}
                          onChange={(e) => setSignUpData(prev => ({ ...prev, email: e.target.value }))}
                          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                          placeholder="your.email@example.com"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Phone / WhatsApp number <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="tel"
                          value={signUpData.phone}
                          onChange={(e) => setSignUpData(prev => ({ ...prev, phone: e.target.value }))}
                          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); goNextSignup(); } }}
                          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                          placeholder="+234 xxx xxx xxxx"
                        />
                        <p className="text-xs text-gray-500 mt-1">Buyers use this to reach you about orders — a WhatsApp number works great.</p>
                      </div>
                    </div>
                  )}

                  {signupStep === 2 && (
                    <div className="space-y-5">
                      <div className="text-center">
                        <div className="text-4xl mb-2">🔒</div>
                        <h1 className="text-2xl font-bold text-gray-900">Lock it down</h1>
                        <p className="text-gray-500 text-sm mt-1">Make a password only you would know.</p>
                      </div>
                      <div>
                        <div className="relative">
                          <input
                            type={showSignupPwd ? 'text' : 'password'}
                            autoFocus
                            value={signUpData.password}
                            onChange={(e) => setSignUpData(prev => ({ ...prev, password: e.target.value }))}
                            className="w-full px-4 py-3 pr-11 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                            placeholder="Create a strong password"
                          />
                          <button
                            type="button"
                            onClick={() => setShowSignupPwd(s => !s)}
                            className="absolute inset-y-0 right-0 px-3 flex items-center text-gray-500 hover:text-gray-700"
                            aria-label={showSignupPwd ? 'Hide password' : 'Show password'}
                            tabIndex={-1}
                          >
                            <i className={`ri-${showSignupPwd ? 'eye-off-line' : 'eye-line'} text-lg`}></i>
                          </button>
                        </div>
                        <div className="flex gap-1 mt-2">
                          {[0, 1, 2, 3].map(i => (
                            <motion.div
                              key={i}
                              initial={false}
                              animate={{
                                backgroundColor:
                                  i < passwordStrength
                                    ? passwordStrength >= 3 ? '#10b981' : passwordStrength === 2 ? '#f59e0b' : '#ef4444'
                                    : '#e5e7eb'
                              }}
                              transition={{ duration: 0.25 }}
                              className="flex-1 h-1.5 rounded-full"
                            />
                          ))}
                        </div>
                        <p className="text-xs text-gray-500 mt-1">
                          {passwordStrength === 0 && 'At least 8 characters, 1 number, and 1 symbol.'}
                          {passwordStrength === 1 && 'Getting there…'}
                          {passwordStrength === 2 && 'Decent — add a symbol or more length.'}
                          {passwordStrength === 3 && '💪 Strong password!'}
                          {passwordStrength === 4 && '🔥 Excellent password!'}
                        </p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Confirm Password</label>
                        <div className="relative">
                          <input
                            type={showSignupConfirmPwd ? 'text' : 'password'}
                            value={signUpData.confirmPassword}
                            onChange={(e) => setSignUpData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); goNextSignup(); } }}
                            className="w-full px-4 py-3 pr-11 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                            placeholder="Type it again"
                          />
                          <button
                            type="button"
                            onClick={() => setShowSignupConfirmPwd(s => !s)}
                            className="absolute inset-y-0 right-0 px-3 flex items-center text-gray-500 hover:text-gray-700"
                            aria-label={showSignupConfirmPwd ? 'Hide password' : 'Show password'}
                            tabIndex={-1}
                          >
                            <i className={`ri-${showSignupConfirmPwd ? 'eye-off-line' : 'eye-line'} text-lg`}></i>
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  {signupStep === 3 && (
                    <div className="space-y-5">
                      <div className="text-center">
                        <div className="text-4xl mb-2">🎓</div>
                        <h1 className="text-2xl font-bold text-gray-900">Your school</h1>
                        <p className="text-gray-500 text-sm mt-1">Every seller is verified as a real student.</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">University / Institution</label>
                        <div className="relative">
                          <input
                            type="text"
                            autoComplete="off"
                            value={verificationData.university}
                            onChange={(e) => { setVerificationData(prev => ({ ...prev, university: e.target.value })); setShowUniSuggestions(true); }}
                            onFocus={() => setShowUniSuggestions(true)}
                            onBlur={() => setTimeout(() => setShowUniSuggestions(false), 150)}
                            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                            placeholder="Type your school or city — e.g. Lagos, Zaria, OAU"
                          />
                          <AnimatePresence>
                            {showUniSuggestions && uniSuggestions.length > 0 && (
                              <motion.ul
                                initial={{ opacity: 0, y: -4 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -4 }}
                                transition={{ duration: 0.15 }}
                                className="absolute left-0 right-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-lg z-20 max-h-64 overflow-auto"
                              >
                                {uniSuggestions.map((u) => (
                                  <li key={u}>
                                    <button
                                      type="button"
                                      onMouseDown={(e) => e.preventDefault()}
                                      onClick={() => { setVerificationData(prev => ({ ...prev, university: u })); setShowUniSuggestions(false); }}
                                      className="w-full text-left px-4 py-2.5 hover:bg-teal-50 text-sm text-gray-700 flex items-center gap-2"
                                    >
                                      <i className="ri-graduation-cap-line text-teal-500 flex-shrink-0"></i>
                                      <span className="truncate">{u}</span>
                                    </button>
                                  </li>
                                ))}
                              </motion.ul>
                            )}
                          </AnimatePresence>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">Search by city too. Type "Other" if your school isn't listed.</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Student ID Number</label>
                        <input
                          type="text"
                          value={verificationData.studentNumber}
                          onChange={(e) => setVerificationData(prev => ({ ...prev, studentNumber: e.target.value }))}
                          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); goNextSignup(); } }}
                          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                          placeholder="Enter your student ID number"
                        />
                      </div>
                    </div>
                  )}

                  {signupStep === 4 && (
                    <div className="space-y-5">
                      <div className="text-center">
                        <div className="text-4xl mb-2">🪪</div>
                        <h1 className="text-2xl font-bold text-gray-900">Upload your student ID</h1>
                        <p className="text-gray-500 text-sm mt-1">Required — a clear photo so we can verify you.</p>
                      </div>
                      <div className="border-2 border-dashed border-gray-300 rounded-xl p-4 sm:p-6 text-center">
                        <i className="ri-file-upload-line text-4xl text-gray-400 mb-3"></i>
                        <p className="text-gray-600 mb-4">Upload a clear photo of your student ID</p>
                        <input type="file" id="signupStudentId" accept="image/*" onChange={handleStudentIdUpload} className="hidden" />
                        <label
                          htmlFor="signupStudentId"
                          className="inline-flex items-center px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 cursor-pointer whitespace-nowrap w-full sm:w-auto justify-center"
                        >
                          <i className="ri-upload-line mr-2"></i>
                          Choose File
                        </label>
                        {verificationData.studentId && (
                          <>
                            <div className="mt-2 text-sm text-green-600 truncate max-w-full">✓ {verificationData.studentId.name}</div>
                            <div className="mt-4 mx-auto w-full max-w-xs sm:max-w-sm">
                              <div className="aspect-[4/3] bg-gray-50 border rounded-lg overflow-hidden flex items-center justify-center">
                                <img src={URL.createObjectURL(verificationData.studentId)} alt="Student ID preview" className="w-full h-full object-contain" />
                              </div>
                            </div>
                          </>
                        )}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Referral code <span className="text-gray-400 font-normal">(optional)</span></label>
                        <input
                          type="text"
                          value={signUpData.referralCode}
                          onChange={(e) => setSignUpData(prev => ({ ...prev, referralCode: e.target.value }))}
                          className="w-full px-4 py-3 uppercase tracking-widest border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                          placeholder="REFERRAL CODE"
                        />
                      </div>
                    </div>
                  )}

                  {signupStep === 5 && (
                    <div className="space-y-5">
                      <div className="text-center">
                        <motion.div
                          initial={{ scale: 0, rotate: -20 }}
                          animate={{ scale: 1, rotate: 0 }}
                          transition={{ type: 'spring', stiffness: 260, damping: 14, delay: 0.05 }}
                          className="text-5xl mb-2"
                        >
                          🚀
                        </motion.div>
                        <h1 className="text-2xl font-bold text-gray-900">Ready to launch!</h1>
                        <p className="text-gray-500 text-sm mt-1">Quick check, then you’re in.</p>
                      </div>
                      <div className="bg-gray-50 rounded-xl p-4 space-y-2 text-sm">
                        <div className="flex justify-between gap-3">
                          <span className="text-gray-500">Name</span>
                          <span className="font-medium text-gray-900 truncate">{signUpData.fullName}</span>
                        </div>
                        <div className="flex justify-between gap-3">
                          <span className="text-gray-500">Email</span>
                          <span className="font-medium text-gray-900 truncate">{signUpData.email}</span>
                        </div>
                        <div className="flex justify-between gap-3">
                          <span className="text-gray-500">Phone</span>
                          <span className="font-medium text-gray-900 truncate">{signUpData.phone}</span>
                        </div>
                        <div className="flex justify-between gap-3">
                          <span className="text-gray-500">School</span>
                          <span className="font-medium text-gray-900 truncate">{verificationData.university}</span>
                        </div>
                        <div className="flex justify-between gap-3">
                          <span className="text-gray-500">Student ID</span>
                          <span className="font-medium text-green-600 truncate">{verificationData.studentId ? '✓ Uploaded' : 'Missing'}</span>
                        </div>
                        {signUpData.referralCode && (
                          <div className="flex justify-between gap-3">
                            <span className="text-gray-500">Referral</span>
                            <span className="font-medium text-teal-600 truncate">{signUpData.referralCode}</span>
                          </div>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => handleSignUp({ preventDefault: () => {} } as React.FormEvent)}
                        disabled={loading}
                        className="w-full px-4 py-3 bg-gradient-to-r from-teal-500 to-emerald-500 text-white rounded-xl hover:opacity-95 transition-opacity font-semibold shadow-lg shadow-teal-500/30 cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
                      >
                        {loading ? 'Creating your account…' : 'Create my seller account 🎉'}
                      </button>
                    </div>
                  )}
                </motion.div>
              </AnimatePresence>
            </div>

            <AnimatePresence>
              {stepError && (
                <motion.div
                  key={stepError}
                  initial={{ opacity: 0, y: -8, x: 0 }}
                  animate={{ opacity: 1, y: 0, x: [0, -8, 8, -6, 6, -3, 3, 0] }}
                  exit={{ opacity: 0, y: -4 }}
                  transition={{ x: { duration: 0.45 }, opacity: { duration: 0.2 }, y: { duration: 0.2 } }}
                  className="mt-3 flex items-start gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2.5"
                >
                  <i className="ri-error-warning-fill text-red-500 text-base flex-shrink-0 mt-0.5"></i>
                  <span className="font-medium">{stepError}</span>
                </motion.div>
              )}
            </AnimatePresence>

            {signupStep < SIGNUP_TOTAL_STEPS - 1 && (
              <div className="flex items-center justify-between gap-3 mt-6">
                <button
                  type="button"
                  onClick={goPrevSignup}
                  disabled={signupStep === 0}
                  className="px-4 py-2.5 text-gray-600 rounded-xl hover:bg-gray-100 transition-colors cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  ← Back
                </button>
                <button
                  type="button"
                  onClick={goNextSignup}
                  className="px-6 py-2.5 bg-teal-600 text-white rounded-xl hover:bg-teal-700 transition-colors font-medium shadow-md shadow-teal-500/20 cursor-pointer"
                >
                  Next →
                </button>
              </div>
            )}

            {signupStep === SIGNUP_TOTAL_STEPS - 1 && (
              <div className="flex items-center justify-start mt-4">
                <button
                  type="button"
                  onClick={goPrevSignup}
                  className="px-4 py-2 text-sm text-gray-500 hover:text-gray-700 cursor-pointer"
                >
                  ← Edit details
                </button>
              </div>
            )}

            <p className="text-center text-xs text-gray-500 mt-6">
              Already have an account?{' '}
              <button
                onClick={() => setCurrentStep('login')}
                className="text-teal-600 hover:text-teal-700 font-medium cursor-pointer"
              >
                Log in
              </button>
            </p>

          </div>
        </main>

        <Footer />
      </div>
    );
  }

  // Login Screen
  if (currentStep === 'login') {
    return (
      <div className="min-h-screen bg-white">
        <CampusHeader />
        
        <main className="max-w-md mx-auto px-4 py-8 md:py-12">
          <div className="bg-white rounded-lg shadow-lg p-6 md:p-8">
            <div className="text-center mb-8">
              <h1 className="text-2xl font-bold text-gray-900 mb-2">Welcome Back</h1>
              <p className="text-gray-600">Login to your seller account</p>
            </div>

            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input
                  type="email"
                  value={loginData.email}
                  onChange={(e) => setLoginData(prev => ({...prev, email: e.target.value}))}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  placeholder="your.email@example.com"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
                <div className="relative">
                  <input
                    type={showLoginPwd ? 'text' : 'password'}
                    value={loginData.password}
                    onChange={(e) => setLoginData(prev => ({...prev, password: e.target.value}))}
                    className="w-full px-4 py-3 pr-11 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    placeholder="Enter your password"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowLoginPwd(s => !s)}
                    className="absolute inset-y-0 right-0 px-3 flex items-center text-gray-500 hover:text-gray-700"
                    aria-label={showLoginPwd ? 'Hide password' : 'Show password'}
                    tabIndex={-1}
                  >
                    <i className={`ri-${showLoginPwd ? 'eye-off-line' : 'eye-line'} text-lg`}></i>
                  </button>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <label className="flex items-center">
                  <input type="checkbox" className="rounded border-gray-300 text-teal-600 focus:ring-teal-500" />
                  <span className="ml-2 text-sm text-gray-600">Remember me</span>
                </label>
                <button type="button" className="text-sm text-teal-600 hover:text-teal-700 cursor-pointer">
                  Forgot password?
                </button>
              </div>

              <AnimatePresence>
                {loginError && (
                  <motion.div
                    key={loginError}
                    initial={{ opacity: 0, y: -8, x: 0 }}
                    animate={{ opacity: 1, y: 0, x: [0, -8, 8, -6, 6, -3, 3, 0] }}
                    exit={{ opacity: 0, y: -4 }}
                    transition={{ x: { duration: 0.45 }, opacity: { duration: 0.2 }, y: { duration: 0.2 } }}
                    className="flex items-start gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2.5"
                  >
                    <i className="ri-error-warning-fill text-red-500 text-base flex-shrink-0 mt-0.5"></i>
                    <span className="font-medium">{loginError}</span>
                  </motion.div>
                )}
              </AnimatePresence>

              <button
                type="submit"
                className="w-full px-4 py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors font-medium whitespace-nowrap cursor-pointer"
              >
                Login
              </button>
            </form>

            <div className="mt-6">
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-white text-gray-500">Or continue with</span>
                </div>
              </div>

              <button className="w-full mt-4 px-4 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center justify-center space-x-2 cursor-pointer">
                <i className="ri-google-fill text-red-500"></i>
                <span>Login with Google</span>
              </button>
            </div>

            <p className="text-center text-sm text-gray-600 mt-6">
              Don't have an account?{' '}
              <button
                onClick={() => setCurrentStep('signup')}
                className="text-teal-600 hover:text-teal-700 font-medium cursor-pointer"
              >
                Sign up here
              </button>
            </p>
          </div>
        </main>

        <Footer />
      </div>
    );
  }

  // Verification Screen
  if (currentStep === 'verification') {
    return (
      <div className="min-h-screen bg-white">
        <CampusHeader />
        
        <main className="max-w-2xl mx-auto px-4 py-8 md:py-12">
          <div className="bg-white rounded-lg shadow-lg p-6 md:p-8">
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-shield-check-line text-2xl text-blue-600"></i>
              </div>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">Verify Your Student Status</h1>
              <p className="text-gray-600">Upload your student ID for verification (reviewed within 24 hours)</p>
            </div>

            <form onSubmit={handleVerification} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">University/Institution</label>
                <div className="relative">
                  <input
                    type="text"
                    autoComplete="off"
                    value={verificationData.university}
                    onChange={(e) => { setVerificationData(prev => ({...prev, university: e.target.value})); setShowUniSuggestions(true); }}
                    onFocus={() => setShowUniSuggestions(true)}
                    onBlur={() => setTimeout(() => setShowUniSuggestions(false), 150)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    placeholder="Type your school or city — e.g. Lagos, Zaria, OAU"
                    required
                  />
                  <AnimatePresence>
                    {showUniSuggestions && uniSuggestions.length > 0 && (
                      <motion.ul
                        initial={{ opacity: 0, y: -4 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -4 }}
                        transition={{ duration: 0.15 }}
                        className="absolute left-0 right-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-lg z-20 max-h-64 overflow-auto"
                      >
                        {uniSuggestions.map((u) => {
                          const q = verificationData.university.trim();
                          const idx = q ? u.toLowerCase().indexOf(q.toLowerCase()) : -1;
                          return (
                            <li key={u}>
                              <button
                                type="button"
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={() => {
                                  setVerificationData(prev => ({...prev, university: u}));
                                  setShowUniSuggestions(false);
                                }}
                                className="w-full text-left px-4 py-2.5 hover:bg-teal-50 text-sm text-gray-700 flex items-center gap-2"
                              >
                                <i className="ri-graduation-cap-line text-teal-500 flex-shrink-0"></i>
                                <span className="truncate">
                                  {idx >= 0 ? (
                                    <>
                                      {u.slice(0, idx)}
                                      <span className="font-semibold text-teal-600">{u.slice(idx, idx + q.length)}</span>
                                      {u.slice(idx + q.length)}
                                    </>
                                  ) : u}
                                </span>
                              </button>
                            </li>
                          );
                        })}
                      </motion.ul>
                    )}
                  </AnimatePresence>
                </div>
                <p className="text-xs text-gray-500 mt-1">Tip: you can search by city too (e.g. "Lagos", "Zaria"). Type "Other" if your school isn't listed.</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Student ID Number</label>
                <input
                  type="text"
                  value={verificationData.studentNumber}
                  onChange={(e) => setVerificationData(prev => ({...prev, studentNumber: e.target.value}))}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  placeholder="Enter your student ID number"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Upload Student ID <span className="text-red-500">*</span></label>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 sm:p-6 text-center">
                  <i className="ri-file-upload-line text-4xl text-gray-400 mb-4"></i>
                  <p className="text-gray-600 mb-4">Upload a clear photo of your student ID</p>
                  <input
                    type="file"
                    id="studentId"
                    accept="image/*"
                    onChange={handleStudentIdUpload}
                    className="hidden"
                    required
                  />
                  <label
                    htmlFor="studentId"
                    className="inline-flex items-center px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 cursor-pointer whitespace-nowrap w-full sm:w-auto justify-center"
                  >
                    <i className="ri-upload-line mr-2"></i>
                    Choose File
                  </label>
                  {verificationData.studentId && (
                    <>
                      <div className="mt-2 text-sm text-green-600 truncate max-w-full">✓ {verificationData.studentId.name}</div>
                      <div className="mt-4 mx-auto w-full max-w-xs sm:max-w-sm">
                        <div className="aspect-[4/3] bg-gray-50 border rounded-lg overflow-hidden flex items-center justify-center">
                          <img
                            src={URL.createObjectURL(verificationData.studentId)}
                            alt="Student ID preview"
                            className="w-full h-full object-contain"
                          />
                        </div>
                      </div>
                    </>
                  )}
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  Accepted formats: JPG, PNG. Max size: 5MB. Your ID will be kept secure and only used for verification.
                </p>
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <div className="flex items-start">
                  <i className="ri-information-line text-yellow-600 mt-0.5 mr-3"></i>
                  <div>
                    <h4 className="text-sm font-medium text-yellow-800">Verification Process</h4>
                    <p className="text-sm text-yellow-700 mt-1">
                      Our team will review your submission within 24 hours. You'll receive an email once approved.
                    </p>
                  </div>
                </div>
              </div>

              <AnimatePresence>
                {verifyError && (
                  <motion.div
                    key={verifyError}
                    initial={{ opacity: 0, y: -8, x: 0 }}
                    animate={{ opacity: 1, y: 0, x: [0, -8, 8, -6, 6, -3, 3, 0] }}
                    exit={{ opacity: 0, y: -4 }}
                    transition={{ x: { duration: 0.45 }, opacity: { duration: 0.2 }, y: { duration: 0.2 } }}
                    className="flex items-start gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2.5"
                  >
                    <i className="ri-error-warning-fill text-red-500 text-base flex-shrink-0 mt-0.5"></i>
                    <span className="font-medium">{verifyError}</span>
                  </motion.div>
                )}
              </AnimatePresence>

              <button
                type="submit"
                disabled={loading || !verificationData.studentId}
                className="w-full px-4 py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors font-medium whitespace-nowrap cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {loading ? 'Submitting…' : !verificationData.studentId ? 'Upload your Student ID to continue' : 'Submit for Verification'}
              </button>
            </form>
          </div>
        </main>

        <Footer />
      </div>
    );
  }

  // Dashboard Screen (After verification)
  if (currentStep === 'dashboard') {
    return (
      <div className="min-h-screen bg-gray-50">
        <CampusHeader />
        
        <main className="max-w-6xl mx-auto px-4 py-8">
          <div className="mb-8">
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">Welcome to Your Seller Dashboard!</h1>
            <p className="text-gray-600">Your account is verified. Ready to post your first item for free!</p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Quick Stats */}
            <div className="lg:col-span-2">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                <div className="bg-white rounded-lg p-6 shadow-sm">
                  <div className="flex items-center">
                    <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center">
                      <i className="ri-store-line text-teal-600 text-xl"></i>
                    </div>
                    <div className="ml-4">
                      <p className="text-sm text-gray-600">Active Listings</p>
                      <p className="text-2xl font-bold text-gray-900">{activeListingsCount}</p>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-lg p-6 shadow-sm">
                  <div className="flex items-center">
                    <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                      <i className="ri-money-dollar-circle-line text-green-600 text-xl"></i>
                    </div>
                    <div className="ml-4">
                      <p className="text-sm text-gray-600">Total Earnings</p>
                      <p className="text-2xl font-bold text-gray-900">₦{totalEarnings.toLocaleString()}</p>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-lg p-6 shadow-sm">
                  <div className="flex items-center">
                    <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                      <i className="ri-eye-line text-blue-600 text-xl"></i>
                    </div>
                    <div className="ml-4">
                      <p className="text-sm text-gray-600">Profile Views</p>
                      <p className="text-2xl font-bold text-gray-900">{profileViews}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Quick Actions */}
              <div className="bg-white rounded-lg shadow-sm p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-6">Quick Actions</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <button onClick={() => setShowCreateModal(true)} className="flex items-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer">
                    <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center mr-4">
                      <i className="ri-add-line text-teal-600"></i>
                    </div>
                    <div className="text-left">
                      <p className="font-medium text-gray-900">Post New Item</p>
                      <p className="text-sm text-gray-600">Create your first listing</p>
                    </div>
                  </button>

                  <button onClick={() => setShowPhotoTipsModal(true)} className="flex items-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mr-4">
                      <i className="ri-camera-line text-blue-600"></i>
                    </div>
                    <div className="text-left">
                      <p className="font-medium text-gray-900">Photo Tips</p>
                      <p className="text-sm text-gray-600">Learn to take great photos</p>
                    </div>
                  </button>
                </div>
              </div>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Free Tier Status */}
              <div className="bg-gradient-to-br from-teal-500 to-blue-600 rounded-lg p-6 text-white">
                <h3 className="text-lg font-semibold mb-2">Free Plan — all features unlocked</h3>
                <ul className="text-sm space-y-1 mb-4">
                  <li>• Unlimited listings</li>
                  <li>• Full marketplace access</li>
                  <li>• No subscription needed</li>
                </ul>
                <div className="w-full px-4 py-2 bg-white/15 text-white rounded-lg font-medium text-center text-sm">
                  Everything's free right now · Pro plans coming next year
                </div>
              </div>

              {/* Tips */}
              <div className="bg-white rounded-lg shadow-sm p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Selling Tips</h3>
                <ul className="space-y-3 text-sm text-gray-600">
                  <li className="flex items-start">
                    <i className="ri-check-line text-green-500 mr-2 mt-0.5"></i>
                    Take clear, well-lit photos
                  </li>
                  <li className="flex items-start">
                    <i className="ri-check-line text-green-500 mr-2 mt-0.5"></i>
                    Write detailed descriptions
                  </li>
                  <li className="flex items-start">
                    <i className="ri-check-line text-green-500 mr-2 mt-0.5"></i>
                    Price competitively
                  </li>
                  <li className="flex items-start">
                    <i className="ri-check-line text-green-500 mr-2 mt-0.5"></i>
                    Respond quickly to messages
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </main>

        {/* Create Listing Modal */}
        {showCreateModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
            <div className="bg-white w-full max-w-2xl rounded-lg shadow-lg p-6 mx-auto max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Post New Item</h3>
                <button onClick={() => setShowCreateModal(false)} className="text-gray-500 hover:text-gray-700">
                  <i className="ri-close-line text-xl"></i>
                </button>
              </div>

              <form onSubmit={handleCreateListing} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                    <input
                      type="text"
                      value={newListing.title}
                      onChange={(e)=>setNewListing(prev=>({...prev, title: e.target.value}))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                      placeholder="e.g., HP Laptop 13" required />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Price (₦)</label>
                    <input
                      type="number"
                      value={newListing.price}
                      onChange={(e)=>setNewListing(prev=>({...prev, price: e.target.value}))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                      placeholder="e.g., 55000" required />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                    <select
                      className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
                      value={newListing.category}
                      onChange={(e) => setNewListing(prev => ({ ...prev, category: e.target.value }))}
                    >
                      <option value="">Select a category</option>
                      <option value="Textbooks">Textbooks</option>
                      <option value="Electronics">Electronics</option>
                      <option value="Furniture">Furniture</option>
                      <option value="Clothing">Clothing</option>
                      <option value="Sport">Sport</option>
                      <option value="Event Ticket">Event Ticket</option>
                      <option value="Hostel">Hostel</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Condition</label>
                    <select
                      value={(newListing.condition || '').toLowerCase()}
                      onChange={(e)=>setNewListing(prev=>({...prev, condition: e.target.value.toLowerCase()}))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    >
                      <option value="">Select condition</option>
                      <option value="new">New</option>
                      <option value="neatly used">Neatly Used</option>
                      <option value="used">Used</option>
                      <option value="refurbished">Refurbished</option>
                      <option value="open box">Open Box</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
                  <input
                    type="text"
                    value={newListing.location}
                    onChange={(e)=>setNewListing(prev=>({...prev, location: e.target.value}))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    placeholder="e.g., OAU Campus Gate" />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <textarea
                    value={newListing.description}
                    onChange={(e)=>setNewListing(prev=>({...prev, description: e.target.value}))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    rows={3}
                    placeholder="Add details buyers should know" />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Upload Photos</label>
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center">
                    <input type="file" id="listingImages" accept="image/*" multiple className="hidden" onChange={handleListingImagesChange} />
                    <label htmlFor="listingImages" className="inline-flex items-center px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 cursor-pointer whitespace-nowrap">
                      <i className="ri-upload-line mr-2"></i>
                      Choose Files
                    </label>
                    {listingImages.length > 0 && (
                      <p className="text-sm text-green-600 mt-2">{listingImages.length} file(s) selected</p>
                    )}
                  </div>
                  {listingImages.length > 0 && (
                    <div className="mt-3 grid grid-cols-3 gap-2">
                      {listingImages.map((f, i) => (
                        <div key={i} className="text-xs text-gray-600 truncate">{f.name}</div>
                      ))}
                    </div>
                  )}
                </div>

                {uploadError && (
                  <div className="text-sm text-red-600">{uploadError}</div>
                )}

                <div className="flex items-center justify-end space-x-3">
                  <button type="button" onClick={()=>setShowCreateModal(false)} className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50">Cancel</button>
                  <button type="submit" disabled={uploading} className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700">
                    {uploading ? 'Posting...' : 'Post Listing'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Onboarding Success Modal */}
        <SellerOnboardingSuccessModal
          isOpen={showSellerSuccessModal}
          onClose={() => setShowSellerSuccessModal(false)}
          onGoDashboard={() => { setShowSellerSuccessModal(false); navigate('/seller-dashboard'); }}
          onUploadDocs={() => { setShowSellerSuccessModal(false); navigate('/sell?step=verification&resume=docs'); }}
        />

        <Footer />
      </div>
    );
  }

  const PhotoTipsModal = ({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) => {
    if (!isOpen) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg p-6 md:p-8 max-w-md w-full relative">
          <button
            onClick={onClose}
            className="absolute top-3 right-3 text-gray-500 hover:text-gray-700 text-2xl"
          >
            &times;
          </button>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Photo Tips</h2>
          <ul className="list-disc list-inside space-y-2 text-gray-700">
            <li>Use good lighting: Natural light is best. Avoid harsh shadows.</li>
            <li>Clean your item: Make sure the item is free of dust and smudges.</li>
            <li>Multiple angles: Take photos from different angles to show all features.</li>
            <li>Show imperfections: Be honest about any flaws or damage.</li>
            <li>Simple background: Use a plain, uncluttered background to make your item stand out.</li>
            <li>High resolution: Use high-resolution images for better clarity.</li>
          </ul>
          <button
            onClick={onClose}
            className="mt-6 w-full bg-teal-600 text-white py-3 px-4 rounded-lg hover:bg-teal-700 transition-colors font-medium"
          >
            Got It!
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col items-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl w-full bg-white p-8 rounded-xl shadow-lg">
        <h1 className="text-3xl font-extrabold text-gray-900 text-center mb-8">Post New Item</h1>
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="title" className="block text-sm font-medium text-gray-700">Title</label>
              <input
                type="text"
                id="title"
                name="title"
                value={newListing.title}
                onChange={handleChange}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-teal-500 focus:border-teal-500 sm:text-sm"
                placeholder="e.g., Powerbank"
                required
              />
            </div>
            <div>
              <label htmlFor="price" className="block text-sm font-medium text-gray-700">Price (N)</label>
              <input
                type="number"
                id="price"
                name="price"
                value={newListing.price}
                onChange={handleChange}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-teal-500 focus:border-teal-500 sm:text-sm"
                placeholder="e.g., 20000"
                required
              />
            </div>
          </div>

          <div>
            <label htmlFor="category" className="block text-sm font-medium text-gray-700">Category</label>
            <select
              id="category"
              name="category"
              value={newListing.category}
              onChange={handleChange}
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-teal-500 focus:border-teal-500 sm:text-sm"
              required
            >
              <option value="">Select a category</option>
              <option value="textbooks">Textbooks</option>
              <option value="electronics">Electronics</option>
              <option value="furniture">Furniture</option>
              <option value="clothing">Clothing</option>
              <option value="sport">Sport</option>
              <option value="event_ticket">Event Ticket</option>
              <option value="hostel">Hostel</option>
            </select>
          </div>

          <div>
            <label htmlFor="condition" className="block text-sm font-medium text-gray-700">Condition</label>
            <select
              id="condition"
              name="condition"
              value={newListing.condition}
              onChange={handleChange}
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-teal-500 focus:border-teal-500 sm:text-sm"
              required
            >
              <option value="">Select condition</option>
              <option value="new">New</option>
              <option value="like_new">Like New</option>
              <option value="used">Used</option>
            </select>
          </div>

          <div>
            <label htmlFor="location" className="block text-sm font-medium text-gray-700">Location</label>
            <input
              type="text"
              id="location"
              name="location"
              value={newListing.location}
              onChange={handleChange}
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-teal-500 focus:border-teal-500 sm:text-sm"
              placeholder="e.g., OAU Campus Gate"
              required
            />
          </div>

          <div>
            <label htmlFor="description" className="block text-sm font-medium text-gray-700">Description</label>
            <textarea
              id="description"
              name="description"
              value={newListing.description}
              onChange={handleChange}
              rows={4}
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-teal-500 focus:border-teal-500 sm:text-sm"
              placeholder="Add details buyers should know"
              required
            ></textarea>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Upload Photos</label>
            <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md">
              <div className="space-y-1 text-center">
                <svg
                  className="mx-auto h-12 w-12 text-gray-400"
                  stroke="currentColor"
                  fill="none"
                  viewBox="0 0 48 48"
                  aria-hidden="true"
                >
                  <path
                    d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m-4-4h8m-4 0a4 4 0 11-8 0 4 4 0 018 0z"
                    strokeWidth={2}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <div className="flex text-sm text-gray-600">
                  <label
                    htmlFor="file-upload"
                    className="relative cursor-pointer bg-white rounded-md font-medium text-teal-600 hover:text-teal-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-teal-500"
                  >
                    <span>Upload a file</span>
                    <input
                      id="file-upload"
                      name="file-upload"
                      type="file"
                      multiple
                      className="sr-only"
                      onChange={handleFileChange}
                    />
                  </label>
                  <p className="pl-1">or drag and drop</p>
                </div>
                <p className="text-xs text-gray-500">PNG, JPG, GIF up to 10MB</p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setShowPhotoTipsModal(true)}
              className="mt-2 text-sm text-teal-600 hover:text-teal-500 font-medium"
            >
              Photo Tips
            </button>
          </div>

          {selectedFiles.length > 0 && (
            <div className="mt-6">
              <h3 className="text-lg font-medium text-gray-900">Selected Photos:</h3>
              <ul className="mt-2 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                {selectedFiles.map((file, index) => (
                  <li key={index} className="relative">
                    <img
                      src={URL.createObjectURL(file)}
                      alt={file.name}
                      className="h-32 w-full object-cover rounded-md"
                    />
                    <button
                      type="button"
                      onClick={() => handleRemoveFile(index)}
                      className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 text-xs"
                    >
                      &times;
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="flex justify-end space-x-4 mt-8">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="px-6 py-3 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-3 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-teal-600 hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500"
            >
              {loading ? 'Posting...' : 'Post Listing'}
            </button>
          </div>
        </form>
      </div>
      <PhotoTipsModal isOpen={showPhotoTipsModal} onClose={() => setShowPhotoTipsModal(false)} />
      <ListingSuccessModal
        isOpen={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        listingId={createdListingId}
      />
     </div>
   );
 };
 
 export default SellPage;
